/**
 * Weekly Customer Update — pure assembly + email render engine.
 *
 * WHY: the Governed Weekly Customer Update Email is the panel's #1 P0.
 * This module is its tested core: given an Account and the live boards
 * (tasks / tickets), it assembles an HONEST digest of the trailing week —
 * what shipped, what's in flight, what's coming, and what's stuck — and
 * renders it as a subject + plain-text + inline-styled HTML email.
 *
 * PURE by contract (same discipline as `timeMachine.ts`): no store, no
 * firebase, no network, no writes, no mutation of inputs. Empty buckets
 * stay empty — the engine never fabricates items to make a week look
 * busier than it was. A follow-up atom wires the governed send.
 *
 * Account linkage uses only fields that actually exist on the records:
 *   - ProjectTask → `accountId` (exact) or `accountName` (case-insensitive).
 *   - Ticket has NO account fields, so a ticket ties to the account when a
 *     linked account task references it via `ticketId` (the one real bridge)
 *     or when its `labels` carry the account's id or name.
 */

import type { Account, Person, ProjectTask, Ticket } from '../db/types';

/** One line item in a digest section. */
export interface WeeklyUpdateItem {
  title: string;
  detail?: string;
}

/** The assembled weekly digest for one account. */
export interface WeeklyUpdateDigest {
  accountId: string;
  accountName: string;
  /** Completed / done / closed within the trailing 7 days. */
  delivered: WeeklyUpdateItem[];
  /** Open, actively in-progress work. */
  inProgress: WeeklyUpdateItem[];
  /** Open items due within the next 7 days, or not yet started. */
  next: WeeklyUpdateItem[];
  /** Overdue or explicitly blocked items — the "needs your input" list. */
  blockers: WeeklyUpdateItem[];
  /** One honest health line derived from the week's data, or ''. */
  healthNote: string;
}

const DAY_MS = 86_400_000;
const WEEK_MS = 7 * DAY_MS;

/** Parse an ISO timestamp or date, returning undefined for junk. */
function parseMs(iso: string | null | undefined): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? undefined : t;
}

/** Is a due date (YYYY-MM-DD or ISO) strictly in the past? */
function isPast(iso: string | null | undefined, nowMs: number): boolean {
  const t = parseMs(iso);
  // A date-only due date means "due by end of that day" — grant the full day.
  return t !== undefined && t + DAY_MS <= nowMs;
}

/** Is a due date within the next 7 days (including today)? */
function isDueSoon(iso: string | null | undefined, nowMs: number): boolean {
  const t = parseMs(iso);
  return t !== undefined && t + DAY_MS > nowMs && t <= nowMs + WEEK_MS;
}

/** Does a task belong to this account (by the fields tasks actually have)? */
function taskBelongsToAccount(task: ProjectTask, account: Account): boolean {
  if (task.accountId) return task.accountId === account.id;
  if (task.accountName) return task.accountName.toLowerCase() === account.name.toLowerCase();
  return false;
}

/**
 * Does a ticket belong to this account? Tickets carry no account fields,
 * so the only honest ties are (a) an account task bridging via `ticketId`,
 * or (b) a label carrying the account's id or name.
 */
function ticketBelongsToAccount(ticket: Ticket, account: Account, accountTasks: ProjectTask[]): boolean {
  if (accountTasks.some((t) => t.ticketId === ticket.id)) return true;
  const name = account.name.toLowerCase();
  return ticket.labels.some((l) => l === account.id || l.toLowerCase() === name);
}

function taskItem(task: ProjectTask): WeeklyUpdateItem {
  const item: WeeklyUpdateItem = { title: task.title };
  if (task.dueDate) item.detail = `Due ${task.dueDate}`;
  return item;
}

function ticketItem(ticket: Ticket): WeeklyUpdateItem {
  const item: WeeklyUpdateItem = { title: ticket.title };
  if (ticket.deadline) item.detail = `Due ${ticket.deadline}`;
  return item;
}

/**
 * One honest health line derived from what the week's data actually shows.
 * The full customer-health band (`health.ts`) scores a Person record; an
 * Account carries none of those signals, so we derive the note from the
 * assembled buckets using the same band vocabulary — and return '' when
 * there is nothing to derive from (never an invented "all good!").
 */
function deriveHealthNote(d: Pick<WeeklyUpdateDigest, 'delivered' | 'inProgress' | 'next' | 'blockers'>): string {
  const n = d.blockers.length;
  if (n > 0) {
    return `Being straight with you: ${n} item${n > 1 ? 's are' : ' is'} blocked or overdue — see "Needs your input" below.`;
  }
  if (d.delivered.length > 0) {
    return `Healthy week: ${d.delivered.length} item${d.delivered.length > 1 ? 's' : ''} shipped, nothing blocked.`;
  }
  if (d.inProgress.length > 0 || d.next.length > 0) {
    return 'Steady week: work is in flight, nothing shipped in the trailing seven days, nothing blocked.';
  }
  return '';
}

/**
 * Assemble the weekly digest for one account from the live boards.
 * PURE: reads only its arguments; empty buckets stay empty arrays.
 *
 * Bucket precedence per item (each item lands in exactly one bucket):
 *   delivered (done within trailing 7d) → blockers (blocked / overdue)
 *   → inProgress → next (due within 7d, or not yet started).
 * Done-but-stale items (outside the window) and cancelled tickets are
 * old news, not this week's update — they are excluded, not backfilled.
 */
export function assembleWeeklyUpdate(
  account: Account,
  data: { tasks?: ProjectTask[]; tickets?: Ticket[]; nowMs?: number },
): WeeklyUpdateDigest {
  const nowMs = data.nowMs ?? Date.now();
  const accountTasks = (data.tasks ?? []).filter((t) => taskBelongsToAccount(t, account));
  const accountTickets = (data.tickets ?? []).filter((t) =>
    ticketBelongsToAccount(t, account, accountTasks),
  );

  const delivered: WeeklyUpdateItem[] = [];
  const inProgress: WeeklyUpdateItem[] = [];
  const next: WeeklyUpdateItem[] = [];
  const blockers: WeeklyUpdateItem[] = [];

  for (const task of accountTasks) {
    if (task.status === 'done') {
      const doneAt = parseMs(task.completedAt) ?? parseMs(task.updatedAt);
      if (doneAt !== undefined && nowMs - doneAt <= WEEK_MS && doneAt <= nowMs) {
        delivered.push(taskItem(task));
      }
      continue; // stale wins are old news, not this week's update
    }
    if (task.status === 'blocked' || isPast(task.dueDate, nowMs)) {
      blockers.push(taskItem(task));
    } else if (task.status === 'in-progress') {
      inProgress.push(taskItem(task));
    } else if (isDueSoon(task.dueDate, nowMs) || task.status === 'todo') {
      next.push(taskItem(task));
    }
  }

  for (const ticket of accountTickets) {
    if (ticket.status === 'cancelled') continue;
    if (ticket.status === 'done') {
      const doneAt = parseMs(ticket.updatedAt);
      if (doneAt !== undefined && nowMs - doneAt <= WEEK_MS && doneAt <= nowMs) {
        delivered.push(ticketItem(ticket));
      }
      continue;
    }
    if (ticket.status === 'blocked' || isPast(ticket.deadline, nowMs)) {
      blockers.push(ticketItem(ticket));
    } else if (ticket.status === 'in-progress' || ticket.status === 'review') {
      inProgress.push(ticketItem(ticket));
    } else {
      // backlog / open / claimed — not yet started, or due soon.
      next.push(ticketItem(ticket));
    }
  }

  const buckets = { delivered, inProgress, next, blockers };
  return {
    accountId: account.id,
    accountName: account.name,
    ...buckets,
    healthNote: deriveHealthNote(buckets),
  };
}

// ---------------------------------------------------------------------------
// Recipient resolution
// ---------------------------------------------------------------------------

/** The resolved "send to" for a weekly update. */
export interface WeeklyUpdateRecipient {
  name: string;
  email: string;
}

/** Trimmed string, or undefined when blank/absent. */
function nonBlank(s: string | undefined): string | undefined {
  const t = s?.trim();
  return t ? t : undefined;
}

/**
 * Resolve who a weekly update for `account` should go to. PURE: reads only
 * its arguments, never fabricates an address. Resolution order — most
 * authoritative first:
 *
 *   1. The account's own primary POC (`pocEmail`, the flat inline record).
 *   2. A secondary contact — the one flagged `primary` first, then any
 *      secondary that actually has an email.
 *   3. A Person whose `company` matches the account name (case-insensitive)
 *      and has an email — preferring `customer` records over other categories.
 *
 * Returns null when no email is resolvable anywhere; the UI turns that into
 * an honest disabled send ("No contact email on file"), never a guess.
 */
export function resolveWeeklyUpdateRecipient(
  account: Account,
  people: Person[] = [],
): WeeklyUpdateRecipient | null {
  const pocEmail = nonBlank(account.pocEmail);
  if (pocEmail) {
    return { name: nonBlank(account.pocName) ?? pocEmail, email: pocEmail };
  }

  const secondaries = account.secondaryContacts ?? [];
  const secondary =
    secondaries.find((c) => c.primary && nonBlank(c.email)) ??
    secondaries.find((c) => nonBlank(c.email));
  const secondaryEmail = nonBlank(secondary?.email);
  if (secondary && secondaryEmail) {
    return { name: nonBlank(secondary.name) ?? secondaryEmail, email: secondaryEmail };
  }

  const accountName = account.name.trim().toLowerCase();
  const matches = people.filter(
    (p) => nonBlank(p.email) && (p.company ?? '').trim().toLowerCase() === accountName,
  );
  const person = matches.find((p) => p.category === 'customer') ?? matches[0];
  const personEmail = nonBlank(person?.email);
  if (person && personEmail) {
    return { name: nonBlank(person.name) ?? personEmail, email: personEmail };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Batch assembly (bold bet #1 — fire the weekly update at every account)
// ---------------------------------------------------------------------------

/** The honest reason a batch entry cannot send. */
export const BATCH_SKIP_NO_EMAIL = 'No contact email on file';

/** One account's row in the batch-review list. */
export interface BatchWeeklyUpdateEntry {
  account: Account;
  digest: WeeklyUpdateDigest;
  /** null when no email resolves anywhere — the row is skipped, never guessed. */
  recipient: WeeklyUpdateRecipient | null;
  /** Present exactly when `recipient` is null. */
  skipReason?: string;
}

/**
 * Assemble the weekly digest for EVERY account in one pass. PURE: reads only
 * its arguments, delegates to `assembleWeeklyUpdate` and
 * `resolveWeeklyUpdateRecipient`, and never fabricates an address — an
 * account with no resolvable email gets `recipient: null` plus an honest
 * `skipReason` (its digest still assembles, so the operator can read it).
 * Entries come back alphabetically by account name: a roster, not a ranking.
 */
export function buildBatchWeeklyUpdates(
  accounts: Account[],
  data: { tasks?: ProjectTask[]; tickets?: Ticket[]; people?: Person[]; nowMs?: number } = {},
): BatchWeeklyUpdateEntry[] {
  return [...accounts]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((account) => {
      const recipient = resolveWeeklyUpdateRecipient(account, data.people ?? []);
      const entry: BatchWeeklyUpdateEntry = {
        account,
        digest: assembleWeeklyUpdate(account, {
          tasks: data.tasks,
          tickets: data.tickets,
          ...(data.nowMs !== undefined ? { nowMs: data.nowMs } : {}),
        }),
        recipient,
      };
      if (!recipient) entry.skipReason = BATCH_SKIP_NO_EMAIL;
      return entry;
    });
}

// ---------------------------------------------------------------------------
// Email rendering
// ---------------------------------------------------------------------------

/** Minimal, dependency-free HTML escaping for item titles/details. */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const SECTIONS: { key: keyof Pick<WeeklyUpdateDigest, 'delivered' | 'inProgress' | 'next' | 'blockers'>; heading: string }[] = [
  { key: 'delivered', heading: 'Delivered' },
  { key: 'inProgress', heading: 'In progress' },
  { key: 'next', heading: 'Coming next' },
  { key: 'blockers', heading: 'Needs your input' },
];

const EMPTY_LINE = 'Nothing this week.';

/**
 * Render the digest as an email: subject + plain-text + inline-styled HTML.
 * Every section always renders — an empty one gets an honest
 * "Nothing this week." rather than being silently omitted. The HTML is a
 * single self-contained block: inline styles only, no external assets,
 * item text HTML-escaped.
 */
export function renderWeeklyUpdateEmail(
  digest: WeeklyUpdateDigest,
  opts: { fromName?: string; periodLabel?: string } = {},
): { subject: string; textContent: string; htmlContent: string } {
  const subject = `Your weekly update — ${digest.accountName}`;
  const periodLabel = opts.periodLabel ?? 'This week';

  // ----- Plain text -----
  const textLines: string[] = [`${subject}`, `${periodLabel}`, ''];
  if (digest.healthNote) {
    textLines.push(digest.healthNote, '');
  }
  for (const { key, heading } of SECTIONS) {
    textLines.push(`${heading}:`);
    const items = digest[key];
    if (items.length === 0) {
      textLines.push(`  - ${EMPTY_LINE}`);
    } else {
      for (const item of items) {
        textLines.push(`  - ${item.title}${item.detail ? ` (${item.detail})` : ''}`);
      }
    }
    textLines.push('');
  }
  if (opts.fromName) textLines.push(`— ${opts.fromName}`);
  const textContent = textLines.join('\n').trimEnd() + '\n';

  // ----- HTML (inline styles only, no external assets) -----
  const h: string[] = [];
  h.push(
    '<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;max-width:600px;margin:0 auto;padding:24px;color:#1a1a1a;background:#ffffff;">',
  );
  h.push(
    `<h1 style="font-size:20px;margin:0 0 4px 0;">${escapeHtml(`Your weekly update — ${digest.accountName}`)}</h1>`,
  );
  h.push(`<p style="font-size:13px;color:#666666;margin:0 0 16px 0;">${escapeHtml(periodLabel)}</p>`);
  if (digest.healthNote) {
    h.push(
      `<p style="font-size:14px;margin:0 0 16px 0;padding:10px 12px;background:#f6f6f6;border-left:3px solid #999999;">${escapeHtml(digest.healthNote)}</p>`,
    );
  }
  for (const { key, heading } of SECTIONS) {
    h.push(
      `<h2 style="font-size:15px;margin:20px 0 6px 0;border-bottom:1px solid #e5e5e5;padding-bottom:4px;">${escapeHtml(heading)}</h2>`,
    );
    const items = digest[key];
    if (items.length === 0) {
      h.push(`<p style="font-size:14px;color:#888888;margin:0;">${escapeHtml(EMPTY_LINE)}</p>`);
    } else {
      h.push('<ul style="font-size:14px;margin:0;padding-left:20px;">');
      for (const item of items) {
        const detail = item.detail
          ? ` <span style="color:#888888;">(${escapeHtml(item.detail)})</span>`
          : '';
        h.push(`<li style="margin:2px 0;">${escapeHtml(item.title)}${detail}</li>`);
      }
      h.push('</ul>');
    }
  }
  if (opts.fromName) {
    h.push(`<p style="font-size:14px;margin:24px 0 0 0;">— ${escapeHtml(opts.fromName)}</p>`);
  }
  h.push('</div>');
  const htmlContent = h.join('\n');

  return { subject, textContent, htmlContent };
}
